/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  Button,
  Form,
  Input,
  Select,
  Switch,
  Tabs,
  Typography,
  App
} from 'antd';
import type { TabsProps } from 'antd';
import CustomLabel from '@/components/custom-label';
import { Icon } from '../../components/icons';
import PcIcon from '@/assets/svgs/pc-icon.svg?react';
import MobileIcon from '@/assets/svgs/mobile-icon.svg?react';
import AppIcon from '@/assets/svgs/app-icon.svg?react';
import { useQuery } from '@/hooks/query.hook';
import { PageURLs } from '@/utils/navigate';
import { PermissionMode } from '@/enums/mode.enum';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  createBannerHeader,
  updateBannerHeader,
  getBannerHeaderById,
  getScreenFunctions,
  getDisplayTypes,
  watchBannerHeaderMessage,
  watchBannerHeaderDetail,
  watchScreenFunctions,
  watchDisplayTypes,
  resetMessages
} from '@/redux/slices/banner-header.slice';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';
import { DISPLAY_TYPE_ID } from '@/constants/banner-header.constant';

type BannerHeaderFormData = {
  title: string;
  text: string;
  displayTypeId?: number;
  isActive: boolean;
  url?: string;
  functionLink?: number;
};

export function BannerHeaderCreate() {
  const { push, mode, id } = useQuery();
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState<'PC' | 'MOBILE' | 'APP'>('PC');
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const bannerHeaderMessage = useAppSelector(watchBannerHeaderMessage);
  const bannerHeaderDetail = useAppSelector(watchBannerHeaderDetail);
  const screenFunctions = useAppSelector(watchScreenFunctions);
  const displayTypes = useAppSelector(watchDisplayTypes);

  const title = useMemo(
    () =>
      mode === PermissionMode.EDIT
        ? 'Chỉnh sửa Banner Header'
        : 'Thêm mới Banner Header',
    [mode]
  );

  const handleGoBack = () => {
    push(PageURLs.ofHomeBannerHeader());
  };

  useEffect(() => {
    const initialData = async () => {
      if (mode === PermissionMode.EDIT && id) {
        await dispatch(getBannerHeaderById(id));
      }
    };
    initialData();
  }, [mode, id, dispatch]);

  useEffect(() => {
    if (mode === PermissionMode.EDIT && id) {
      setActiveTab('PC');
    }
  }, [mode, id]);

  useEffect(() => {
    if (mode === PermissionMode.CREATE) {
      form.setFieldsValue({
        title: '',
        text: '',
        displayTypeId: undefined,
        url: undefined,
        functionLink: undefined,
        isActive: true
      });
    }
  }, [mode, form]);

  useEffect(() => {
    if (mode !== PermissionMode.EDIT || !bannerHeaderDetail) return;

    const details = bannerHeaderDetail.details || [];
    const currentDetail = details.find((d) => d.type === activeTab);

    const formData: BannerHeaderFormData = {
      title: bannerHeaderDetail.title,
      text: currentDetail?.content || '',
      displayTypeId: currentDetail?.displayTypeId,
      isActive: currentDetail?.enabled || false,
      url:
        currentDetail?.displayTypeId === DISPLAY_TYPE_ID.TEXT_URL
          ? currentDetail.linkUrl || ''
          : undefined,
      functionLink:
        currentDetail?.displayTypeId === DISPLAY_TYPE_ID.TEXT_FUNCTION_LINK &&
        currentDetail.screensFunction
          ? currentDetail.screensFunction.id
          : undefined
    };

    form.setFieldsValue(formData);
  }, [mode, bannerHeaderDetail, activeTab, form]);

  const handleTabChange = (key: 'PC' | 'MOBILE' | 'APP') => {
    setActiveTab(key);
    if (mode === PermissionMode.CREATE) {
      form.setFieldsValue({
        title: '',
        text: '',
        displayTypeId: undefined,
        url: undefined,
        functionLink: undefined,
        isActive: true
      });
    }
  };

  useEffect(() => {
    if (bannerHeaderMessage) {
      if (bannerHeaderMessage.type === Message.SUCCESS) {
        message.success(bannerHeaderMessage.message);
      } else if (bannerHeaderMessage.type === Message.ERROR) {
        message.error(bannerHeaderMessage.message);
      }
      dispatch(resetMessages());
    }
  }, [bannerHeaderMessage, message, dispatch]);

  useEffect(() => {
    dispatch(getScreenFunctions());
    dispatch(getDisplayTypes());
  }, [dispatch]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();

      const detailPayload: {
        TYPE: 'PC' | 'MOBILE' | 'APP';
        CONTENT: string;
        DISPLAY_TYPE_ID: number;
        ENABLED: boolean;
        LINK_URL?: string;
        SCREENS_FUNCTION_ID?: number;
      } = {
        TYPE: activeTab,
        CONTENT: values.text,
        DISPLAY_TYPE_ID: values.displayTypeId,
        ENABLED: values.isActive
      };

      if (values.displayTypeId === DISPLAY_TYPE_ID.TEXT_URL && values.url) {
        detailPayload.LINK_URL = values.url;
      }

      if (
        values.displayTypeId === DISPLAY_TYPE_ID.TEXT_FUNCTION_LINK &&
        values.functionLink
      ) {
        detailPayload.SCREENS_FUNCTION_ID = values.functionLink;
      }

      if (mode === PermissionMode.EDIT && id) {
        const updatePayload: UpdateBannerHeaderPayload = {
          ID: Number(id),
          TITLE: values.title,
          DETAIL: detailPayload
        };

        await dispatch(updateBannerHeader(updatePayload)).unwrap();
      } else {
        const createPayload: CreateBannerHeaderPayload = {
          TITLE: values.title,
          DETAIL: detailPayload
        };

        await dispatch(createBannerHeader(createPayload)).unwrap();
      }

      handleGoBack();
    } catch (err: any) {
      if (err.errorFields) {
        return;
      }
      message.error(
        mode === PermissionMode.EDIT
          ? MESSAGE.UPDATE_BANNER_HEADER_FAIL
          : MESSAGE.CREATE_BANNER_HEADER_FAIL
      );
    }
  };

  const items: TabsProps['items'] = [
    {
      key: 'PC',
      label: (
        <span className='tab-label'>
          <PcIcon width={14} height={14} />
          Hiển thị cho PC
        </span>
      )
    },
    {
      key: 'MOBILE',
      label: (
        <span className='tab-label'>
          <MobileIcon width={14} height={14} />
          Hiển thị cho Mobile
        </span>
      )
    },
    {
      key: 'APP',
      label: (
        <span className='tab-label'>
          <AppIcon width={14} height={14} />
          Hiển thị cho App
        </span>
      )
    }
  ];

  const displayTypeId = Form.useWatch('displayTypeId', form);

  return (
    <div css={rootStyles}>
      <div css={boxStyles}>
        <Typography.Title css={titleStyles} level={5}>
          <Button
            css={goBackBtnStyles}
            onClick={handleGoBack}
            type='text'
            icon={<Icon type='arrowLeft' width={20} height={20} />}
          />
          {title}
        </Typography.Title>
      </div>
      <div css={boxStyles}>
        <Form form={form} css={titleFormStyles}>
          <Form.Item
            css={largeInputItemStyles}
            label={CustomLabel({
              text: 'Tiêu đề Banner Header',
              isRequired: true
            })}
            name='title'
            rules={[
              { required: true, message: 'Vui lòng nhập tiêu đề Banner Header' }
            ]}
          >
            <Input
              css={inputLargeStyles}
              size='large'
              placeholder='Nhập tiêu đề Banner Header'
              allowClear
            />
          </Form.Item>
        </Form>
        <Tabs
          css={tabStyle}
          items={items}
          activeKey={activeTab}
          onChange={(key) => handleTabChange(key as 'PC' | 'MOBILE' | 'APP')}
        />
      </div>
      <div css={boxStyles}>
        <Form form={form} css={formStyles}>
          <Form.Item
            css={largeInputItemStyles}
            label={CustomLabel({ text: 'Text hiển thị', isRequired: true })}
            name='text'
            rules={[{ required: true, message: 'Vui lòng nhập text hiển thị' }]}
          >
            <Input
              css={inputLargeStyles}
              size='large'
              placeholder='Nhập text hiển thị Banner Header'
              allowClear
            />
          </Form.Item>

          <Form.Item
            label={CustomLabel({ text: 'Kiểu hiển thị', isRequired: true })}
            name='displayTypeId'
            rules={[{ required: true, message: 'Vui lòng chọn kiểu hiển thị' }]}
          >
            <Select
              css={inputStyles}
              placeholder='Chọn kiểu hiển thị Banner Header'
              options={displayTypes.map((dt) => ({
                value: dt.id,
                label: dt.name
              }))}
              allowClear
            />
          </Form.Item>

          {displayTypeId === DISPLAY_TYPE_ID.TEXT_URL && (
            <Form.Item
              label={CustomLabel({ text: 'URL', isRequired: true })}
              name='url'
              rules={[{ required: true, message: 'Vui lòng nhập URL' }]}
            >
              <Input
                css={inputStyles}
                placeholder='Nhập URL cho Banner Header'
                allowClear
              />
            </Form.Item>
          )}

          {displayTypeId === DISPLAY_TYPE_ID.TEXT_FUNCTION_LINK && (
            <Form.Item
              label={CustomLabel({
                text: 'Chức năng liên kết',
                isRequired: true
              })}
              name='functionLink'
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn chức năng liên kết'
                }
              ]}
            >
              <Select
                css={inputStyles}
                placeholder='Chọn chức năng liên kết'
                allowClear
                options={screenFunctions.map((func) => ({
                  label: func.name,
                  value: func.id
                }))}
              />
            </Form.Item>
          )}

          <Form.Item
            css={switchItemStyles}
            label={CustomLabel({ text: 'Hiển thị', isRequired: true })}
            name='isActive'
            valuePropName='checked'
            initialValue
          >
            <Switch />
          </Form.Item>

          <div css={actionsStyles}>
            <Button onClick={handleGoBack}>Hủy</Button>
            <Button type='primary' onClick={onSubmit}>
              Lưu thay đổi
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);

  &:nth-of-type(2) {
    padding-left: 3.5rem;

    .ant-tabs-top > .ant-tabs-nav::before {
      border-bottom: 0 !important;
    }
  }

  &:nth-of-type(3) {
    padding-left: 3.5rem;
  }
`;

const titleStyles = css`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  position: relative;
  left: 2rem;
  margin-bottom: 0 !important;
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }
  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }

  .tab-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    line-height: 1;
  }
  .tab-label svg {
    display: inline-block;
    filter: brightness(0) saturate(100%);
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #fff !important;
  }
  .ant-tabs-tab-active .tab-label svg {
    filter: brightness(0) invert(1);
  }

  .ant-tabs-ink-bar {
    display: none;
  }
  .ant-tabs-tab:hover {
    color: #333 !important;
  }
`;

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .ant-form-item {
    .ant-form-item-label {
      width: 16rem;
      text-align: left;
      font-weight: 500;
      line-height: 3.8rem;

      label {
        &::after,
        &::before {
          display: none !important;
        }
      }
    }
  }
`;

const inputStyles = css`
  height: 3.8rem;
  width: 25rem !important;
`;

const inputLargeStyles = css`
  height: 5rem;
`;

const titleFormStyles = css`
  .ant-form-item {
    margin-bottom: 2.4rem;
  }

  .ant-form-item-label {
    padding-right: 2rem;

    label {
      &::after,
      &::before {
        display: none !important;
      }
    }
  }
`;

const largeInputItemStyles = css`
  .ant-form-item-label {
    line-height: 5rem !important;
  }
`;

const switchItemStyles = css`
  .ant-form-item-control {
    display: flex;
    justify-content: center;
  }
`;

const actionsStyles = css`
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  margin-top: 1.6rem;
`;

const goBackBtnStyles = css`
  position: absolute;
  left: -3rem;
  width: 2.6rem !important;
  height: 2.6rem !important;
  padding: 0;
  svg {
    margin-top: 0.1rem;
  }
  &:hover {
    border-radius: 50%;
  }
`;
